import {
    MRT_Table,
    MRT_TableInstance,
    MRT_TableOptions,
    MRT_RowData,
    useMaterialReactTable,
    MRT_Row,
} from "material-react-table";
import { useTheme } from "@mui/material";
import { IngredientSupplierActivityData } from "src/api/client-api/ingredients/useGetOneIngredient";
import { IngredientSupplierActivityDataRow } from "./IngredientSupplierActivityDataRow";

export type ActivityDataGridProps<T extends MRT_RowData> = MRT_TableOptions<T> & {
    property: "ingredientSupplierActivityDatas" | "productOrganizationActivityDatas";
};

export function ActivityDataGrid<T extends MRT_RowData>(props: ActivityDataGridProps<T>) {
    const theme = useTheme();
    const { property } = props;

    const skipDetailPanel = (row: MRT_Row<T>) => {
        return property !== "ingredientSupplierActivityDatas" || row.original[property].length <= 1;
    };

    let config: MRT_TableOptions<T> = {
        enableDensityToggle: false,
        enableRowActions: true,
        enableColumnActions: false,
        enableSorting: false,
        enableExpanding: true,
        positionExpandColumn: "last",
        muiExpandButtonProps: ({ row }) => ({
            sx: {
                display: skipDetailPanel(row) ? "none" : "inline-flex",
            },
        }),
        renderDetailPanel: ({ row }) => {
            if (skipDetailPanel(row)) {
                return null;
            }

            return row.original[property].map((activityData: IngredientSupplierActivityData, index: number) => {
                return <IngredientSupplierActivityDataRow key={index} activityData={activityData} />;
            });
        },
        positionActionsColumn: "last",
        muiTableProps: {
            sx: {
                border: `1px solid ${theme.custom.grey[500]}`,
                borderRadius: theme.spacing(1),
                overflow: "hidden",
            },
        },
        muiTableHeadRowProps: {
            sx: {
                boxShadow: "none",
            },
        },
        muiTableHeadCellProps: {
            sx: {
                background: theme.custom.grey[200],
                color: theme.custom.grey[2000],
                fontSize: "0.75rem",
                fontWeight: 500,
                padding: `${theme.spacing(1.25)} ${theme.spacing(4)}`,
                align: "center",
                verticalAlign: "middle",
                "& > div": {
                    justifyContent: "flex-start",
                },
            },
        },
        muiTableBodyRowProps: {
            sx: {
                "&:last-child:not(:hover) > td:after": {
                    display: "none",
                },
                "&:last-child > td:after": {
                    display: "inherit",
                },
                "&:first-of-type > td": {
                    paddingTop: theme.spacing(4),
                },
                "&:nth-last-child(2) > td:after": {
                    display: "none",
                },
            },
            hover: true,
        },
        displayColumnDefOptions: {
            "mrt-row-expand": {
                muiTableHeadCellProps: {
                    sx: {
                        "& > div": {
                            display: "none",
                        },
                    },
                },
            },
        },
        muiTableBodyCellProps: (value) => {
            return {
                sx: {
                    "&:first-of-type": {
                        paddingLeft: theme.spacing(4),
                    },
                    "&:last-child": {
                        paddingRight: theme.spacing(4),
                        width: "1%",
                    },
                    borderBottom: "none",
                    padding: theme.spacing(4),
                    ":after": {
                        content: '""',
                        height: "1px",
                        background: "#E7E5E4",
                        position: "absolute",
                        left: value.column.getIsFirstColumn() ? theme.spacing(4) : 0,
                        right: value.column.getIsLastColumn() ? theme.spacing(4) : 0,
                        bottom: 0,
                    },
                },
            };
        },
        localization: {
            actions: "",
        },
        ...props,
    };

    const table: MRT_TableInstance<T> = useMaterialReactTable(config);

    return <MRT_Table table={table} />;
}
